(() => {
  if (window.__rm1PageInit) return;
  window.__rm1PageInit = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const shopRoot = () => window.Shopify?.routes?.root || '/';
  let submitting = false;
  let navObserver;
  let scheduled = false;

  function choose(button, focus = false) {
    const chooser = button.closest('[data-rm1-chooser]');
    if (!chooser) return;
    chooser.querySelectorAll('[data-rm1-choice]').forEach(tab => {
      const selected = tab === button;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    chooser.querySelectorAll('[data-rm1-panel]').forEach(panel => {
      panel.hidden = panel.dataset.rm1Panel !== button.dataset.rm1Choice;
    });
    if (focus) button.focus();
    schedule();
  }

  function updateForm(form) {
    const options = [...form.querySelectorAll('[data-rm1-variant]')].map(select => select.selectedOptions[0]);
    const available = options.length > 0 && options.every(option => option?.dataset.available === 'true');
    const button = form.querySelector('[data-rm1-submit]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.disabled = !available;
    button.textContent = available ? form.dataset.label : form.dataset.soldOut;
    const price = form.querySelector('[data-rm1-total]');
    const sum = options.reduce((total, option) => total + Number(option?.dataset.price || 0), 0);
    if (price && form.dataset.currency) {
      try {
        price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: form.dataset.currency, currencyDisplay: 'code'
        }).format(sum / 100);
      } catch (_) {}
    }
  }

  async function submit(form) {
    const button = form.querySelector('[data-rm1-submit]');
    if (!button || button.disabled || submitting) return;
    const selects = [...form.querySelectorAll('[data-rm1-variant]')];
    if (!selects.length || selects.some(select => select.selectedOptions[0]?.dataset.available !== 'true')) return;
    const ids = selects.map(select => select.value);
    if (ids.some(id => !/^\d+$/.test(id))) return;
    const error = form.querySelector('[data-rm1-cart-error]');
    const cartLink = form.querySelector('[data-rm1-check-cart]');
    submitting = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = form.dataset.adding;
    if (error) error.textContent = '';
    if (cartLink) cartLink.hidden = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map(id => ({ id, quantity: 1 })) }),
        signal: controller.signal
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : form.dataset.error);
      window.location.assign(`${shopRoot()}cart`);
    } catch (err) {
      if (error) error.textContent = err.name === 'AbortError' ? form.dataset.timeout : (err.message && !(err instanceof TypeError) ? err.message : form.dataset.error);
      if (cartLink) cartLink.hidden = false;
      button.removeAttribute('aria-busy');
      submitting = false;
      updateForm(form);
    } finally {
      clearTimeout(timeout);
    }
  }

  document.addEventListener('submit', event => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-rm1-purchase]')) return;
    event.preventDefault();
    submit(event.target);
  });
  document.addEventListener('change', event => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-rm1-variant]')) return;
    const form = event.target.closest('[data-rm1-purchase]');
    if (form) updateForm(form);
  });
  document.addEventListener('keydown', event => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-rm1-choice]')) return;
    const tabs = [...event.target.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    let index = tabs.indexOf(event.target);
    if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = tabs.length - 1;
    else return;
    event.preventDefault();
    choose(tabs[index], true);
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const choice = target.closest('[data-rm1-choice]');
    if (choice) return choose(choice);
    const railButton = target.closest('[data-rm1-rail-step]');
    if (railButton) {
      const rail = document.getElementById(railButton.getAttribute('aria-controls'));
      const first = rail?.querySelector('li');
      if (rail && first) {
        const gap = parseFloat(getComputedStyle(rail).gap) || 20;
        rail.scrollBy({ left: Number(railButton.dataset.rm1RailStep) * (first.getBoundingClientRect().width + gap), behavior: reduceMotion.matches ? 'instant' : 'smooth' });
      }
      return;
    }
    const part = target.closest('[data-rm1-part]');
    if (part) {
      const closer = part.closest('[data-rm1-closer]');
      closer?.querySelectorAll('.rm1-part, .rm1-closer__img').forEach(item => {
        const on = item.dataset.index === part.dataset.rm1Part;
        item.classList.toggle('is-active', on);
        item.querySelector('[data-rm1-part]')?.setAttribute('aria-expanded', String(on));
      });
      // Phones show the image inside the opened part; keep that part in view after the previous one closes.
      const opened = part.closest('.rm1-part');
      if (opened && window.innerWidth < 990) {
        requestAnimationFrame(() => opened.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'instant' : 'smooth' }));
      }
      return;
    }
    const opener = target.closest('[data-rm1-video-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.rm1VideoOpen);
      if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
        dialog.showModal();
        dialog.querySelector('video')?.play().catch(() => {});
      }
      return;
    }
    const close = target.closest('[data-rm1-video-close]');
    if (close) return close.closest('dialog')?.close();
    if (target.matches('dialog.rm1-dialog')) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) target.close();
    }
    const anchor = target.closest('.rm1 a[href^="#"]');
    if (anchor && anchor.hash.length > 1) {
      const section = document.getElementById(decodeURIComponent(anchor.hash.slice(1)));
      if (!section) return;
      event.preventDefault();
      section.scrollIntoView({ behavior: reduceMotion.matches ? 'instant' : 'smooth', block: 'start' });
      const hadTabindex = section.hasAttribute('tabindex');
      if (!hadTabindex) section.tabIndex = -1;
      section.focus({ preventScroll: true });
      if (!hadTabindex) section.addEventListener('blur', () => section.removeAttribute('tabindex'), { once: true });
      history.replaceState(null, '', anchor.hash);
    }
  });
  document.addEventListener('close', event => {
    if (event.target instanceof Element && event.target.matches('.rm1-dialog')) event.target.querySelector('video')?.pause();
  }, true);

  function updateNavigation() {
    const nav = document.querySelector('[data-rm1-nav]');
    if (!nav) return;
    let offset = Number(nav.dataset.minOffset) || 0;
    const headers = [document.querySelector('#shopify-section-header, .shopify-section--header, sticky-header')];
    if (document.querySelector('[data-rm1-detail]')) {
      headers.push(...document.querySelectorAll('.zrx-sticky-atc-bar-top.zrx-sticky-atc-show'));
    }
    headers.filter(Boolean).forEach(header => {
      const position = getComputedStyle(header).position;
      const rect = header.getBoundingClientRect();
      if (header.matches('.zrx-sticky-atc-bar-top.zrx-sticky-atc-show') && position === 'fixed') {
        offset = Math.max(offset, rect.height);
      } else if ((position === 'sticky' || position === 'fixed') && rect.top <= 1 && rect.bottom > 0) {
        offset = Math.max(offset, rect.bottom);
      }
    });
    document.documentElement.style.setProperty('--rm1-header-offset', `${Math.round(offset)}px`);
    const bottom = nav.getBoundingClientRect().bottom;
    const links = [...nav.querySelectorAll('.rm1-localnav__links a[href^="#"]')];
    let active = links[0]?.hash;
    links.forEach(link => {
      const section = document.getElementById(link.hash.slice(1));
      if (section && section.getBoundingClientRect().top <= bottom + 80) active = link.hash;
    });
    links.forEach(link => {
      if (active && link.hash === active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    const bar = document.querySelector('[data-rm1-mbar]');
    if (!bar) return;
    const hero = document.querySelector('[data-rm1-hero-cta]');
    const afterHero = hero && hero.getBoundingClientRect().bottom < bottom;
    const hiderVisible = [...document.querySelectorAll('[data-rm1-hide-bar]')].some(section => {
      const rect = section.getBoundingClientRect();
      return rect.top < window.innerHeight - 90 && rect.bottom > bottom;
    });
    const visible = Boolean(afterHero && !hiderVisible && window.innerWidth < 750);
    bar.classList.toggle('is-visible', visible);
    bar.inert = !visible;
    bar.setAttribute('aria-hidden', String(!visible));
    bar.hidden = false;
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; updateNavigation(); });
  }
  function init() {
    document.querySelectorAll('[data-rm1-purchase]').forEach(updateForm);
    if (navObserver) navObserver.disconnect();
    if ('ResizeObserver' in window) {
      navObserver = new ResizeObserver(schedule);
      document.querySelectorAll('[data-rm1-nav], .shopify-section--header').forEach(element => navObserver.observe(element));
    }
    schedule();
  }
  document.addEventListener('shopify:block:select', event => {
    if (event.target instanceof Element) event.target.querySelector('[data-rm1-part]')?.click();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('scrollend', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

